/* eslint-disable no-console */
/* eslint-disable no-undef */
import {FrontConfig} from '../../../libs/shared/core/src/helpers/bundler/bundlerDefineConfig';
import {isDevelopment, isPreview} from '../../../libs/shared/core/src/helpers/environment/envHelpers';

export enum LegacyApplicationRuntimeMessageTypesEnum {
  HANDSHAKE_ENGINE = 'HANDSHAKE_ENGINE',
  HANDSHAKE_VIEW = 'HANDSHAKE_VIEW',
  APP_LOAD_ERROR = 'APP_LOAD_ERROR',
}

export interface FrontEngineHandshakeModel {
  type: LegacyApplicationRuntimeMessageTypesEnum.HANDSHAKE_ENGINE;
  bundleUrl: string;
}

export interface FrontViewHandshakeModel {
  type: LegacyApplicationRuntimeMessageTypesEnum.HANDSHAKE_VIEW;
  engineIframeName: string;
  widgetId: string;
}

type MessageEventHandler = (event: MessageEvent) => void;
let isHandshakeHandlerRegistered = false;

export function registerHandshakeHandler(
  eventType: LegacyApplicationRuntimeMessageTypesEnum,
  eventHandler: MessageEventHandler,
) {
  if (isHandshakeHandlerRegistered) {
    throw new Error('An event handler was already registered.');
  }

  const oneTimeEventHandler = (event: MessageEvent) => {
    const {origin} = event;

    // Ignore the message if it is not of the right type.
    if (event.data.type !== eventType) {
      return;
    }

    // Ignore the message if it is not from Front and log a warning about it.
    if (!messageEventIsFromFront(event)) {
      console.warn(`Ignoring message ${event.data.type} from ${origin}.`);

      return;
    }

    // Once the first message is handled, we don't need to listen for all messages anymore.
    window.removeEventListener('message', oneTimeEventHandler);

    eventHandler(event);
  };

  window.addEventListener<'message'>('message', oneTimeEventHandler);

  isHandshakeHandlerRegistered = true;
}

const WHITELISTED_ORIGINS = ['myfront.dev', 'frontapp.com'];

function messageEventIsFromFront(event: MessageEvent) {
  // Trust any event when running in development or preview runtime.
  if (isDevelopment() || isPreview()) {
    console.warn('[@frontapp/ui-bridge] Running in Application runtime in a non-secure environment.');
    return true;
  }

  try {
    // We expect the event origin to be a URL
    const origin = new URL(event.origin);

    // We expect messages to come from a subdomain of the whitelisted origins.
    return WHITELISTED_ORIGINS.some((hostname) => origin.hostname.endsWith(`.${hostname}`));
  } catch (error) {
    console.error(`[@frontapp/ui-bridge] Failed to check the origin of message.`, error);
    return false;
  }
}

/**
 * Make sure we are in the right context
 * - Running in an iFrame.
 * - Running in a different domain than its parent's.
 * - Loaded by Front.
 */

export function ensureContextIsFront(): void {
  const {parent, self, location} = window;
  const loadedInIframe = parent !== self;

  if (!loadedInIframe || isRunningOnSameDomain(parent)) {
    throw new Error('This script **MUST** be run in a sandboxed iframe.');
  }

  const loadedByFront =
    location.protocol === 'https:' &&
    // The iFrame is loaded from a dynamic subdomain.
    // We are checking that the host is the same.
    location.host.endsWith(`.${FrontConfig.APPLICATION_RUNTIME_HOST}`);

  if (loadedByFront) {
    return;
  }

  throw new Error('This script **MUST** be run by Front.');
}

export function isRunningOnSameDomain(otherWindow: Window) {
  try {
    // Try to access the window document.
    // If it is accessible, we are running in the same domain.
    // Otherwise, it will trigger a DOMException error.
    return !!otherWindow.document;
  } catch (error) {
    // Ignore cross origin errors, throw the others
    if (isCrossOriginError(error)) {
      return false;
    }

    throw error;
  }
}

// cf: https://developer.mozilla.org/en-US/docs/Web/API/DOMException#exception-SecurityError
function isCrossOriginError(error: Error) {
  return (
    error instanceof DOMException &&
    (error.name === 'SecurityError' || error.name === 'SECURITY_ERR' || error.code === 18)
  );
}
